import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";
import { useCancellationProtectionConfig } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/CancellationProtection/hooks/useCancellationProtectionConfig";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { minutesToMilliseconds } from "date-fns";

import { useFetchPaginatedPlacements } from "../../Placements/api/useFetchPaginatedPlacements";
import { useGetMultipleChoiceAggregates } from "../Review/api/useGetMultipleChoiceAggregates";
import { type QuestionTag } from "../Review/types";
import {
  type AggregatedReviews,
  buildWorkplaceReviewHighlights,
} from "./buildWorkplaceReviewHighlights";
import { WorkplaceHighlightType } from "./constants";
import type { WorkplaceHighlight } from "./types";

interface UseWorkplaceHighlightsProps {
  workplaceId?: string;
  excludeList?: QuestionTag[];
  /**
   * When true, patient ratio highlights are shown as pills.
   * When false or undefined, they are shown in the Patient Load section instead.
   * Set to false when useDelayedReviewUI flag is enabled.
   */
  includePatientRatioHighlights?: boolean;
  /**
   * When false, all internal queries are skipped.
   * Defaults to true.
   */
  enabled?: boolean;
}

interface UseWorkplaceHighlightsResult {
  highlights: WorkplaceHighlight[];
  isLoading: boolean;
}

export function useWorkplaceHighlights(
  props: UseWorkplaceHighlightsProps
): UseWorkplaceHighlightsResult {
  const { workplaceId, excludeList, includePatientRatioHighlights = true, enabled = true } = props;
  const isEnabled = enabled && isDefined(workplaceId);

  // Fetch review highlights data
  const { data: multipleChoiceAggregates = [], isLoading: reviewHighlightsLoading } =
    useGetMultipleChoiceAggregates(workplaceId ?? "", {
      enabled: isEnabled,
      staleTime: minutesToMilliseconds(5),
      useErrorBoundary: false,
    });

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  const { data: workplace } = useGetWorkplace({ workplaceId }, { enabled: isEnabled });
  const { independentContractorBadge, cancellationProtection } = workplace?.data.attributes ?? {};
  const isIndependentContractorBadgesRequired = independentContractorBadge?.enabled ?? false;

  const { enabled: isCancellationProtectionFeatureEnabled } = useCancellationProtectionConfig();
  const isCancellationProtectionEnabled =
    isCancellationProtectionFeatureEnabled &&
    isDefined(cancellationProtection) &&
    cancellationProtection.enabled;

  const qualifications =
    workerQualificationsAreLoaded && workerQualifications
      ? (workerQualifications?.map((q) => q.attributes.name) ?? [])
      : [];

  const { userId: workerId } = useDefinedWorker();
  const {
    data: placementsData,
    isSuccess: isPlacementsSuccess,
    isLoading: isPlacementsLoading,
  } = useFetchPaginatedPlacements(
    {
      workerId,
      filter: { workplaceId },
      placementCandidateId: "",
      shouldLogEvent: false,
    },
    {
      enabled: isEnabled && isDefined(workerId),
    }
  );

  if (!isEnabled) {
    return { highlights: [], isLoading: false };
  }

  const aggregatedReviews: AggregatedReviews[] = multipleChoiceAggregates.map((aggregate) => ({
    tags: aggregate.attributes.question.tags.map(({ tag }) => tag),
    text: aggregate.attributes.text,
    variations: aggregate.attributes.variations as AggregatedReviews["variations"],
  }));

  const reviewHighlights = buildWorkplaceReviewHighlights({
    aggregatedReviews,
    qualifications,
    excludeList,
    mandatoryBreakDurationInMinutes,
    includePatientRatioHighlights,
  });

  const hasPlacements =
    isPlacementsSuccess && placementsData?.pages.some((page) => page.data.length > 0);
  const placementHighlights: WorkplaceHighlight[] = hasPlacements
    ? [
        {
          type: WorkplaceHighlightType.HIRING,
          label: "This workplace is hiring!",
        },
      ]
    : [];

  const highlights = [...placementHighlights, ...reviewHighlights];

  if (isCancellationProtectionEnabled) {
    highlights.push({
      type: WorkplaceHighlightType.CANCELLATION_PROTECTION,
      label: "Cancellation Protection",
    });
  }

  if (isIndependentContractorBadgesRequired) {
    highlights.push({
      type: WorkplaceHighlightType.INDEPENDENT_CONTRACTOR_BADGE_REQUIRED,
      label: "Badge required",
    });
  }

  return {
    isLoading: reviewHighlightsLoading || isPlacementsLoading,
    highlights,
  };
}
